import { useState, useEffect, useCallback } from 'react'
import { Header, Grid, Label, Button } from 'semantic-ui-react'
import { updateTransactionEdit } from '@/actions/transactionEditActions'
import { TRANSACTION_TYPE_OPTIONS } from '@/constants/transactionConstants'
import {
  getTransactionCategoryOptions,
  getTransactionCategorySelector,
} from '@/features/reports/reports.selectors'

import { useReselector } from '@/utils/sharedHooks'
import { Dropdown } from '@/componentsV1/BaseComponents'
import { Transaction } from '@/reducers/admin/allTransactions.slice'
import { TransactionEdit } from '@/reducers/admin/allTransactionEdits.slice'
import { useAppDispatch } from '@/utils/typeHelpers'

export const TransactionEditReview = ({
  transaction,
  initialTransactionEdit,
  handleTransactionEditCompletion,
}: {
  transaction: Transaction
  initialTransactionEdit: TransactionEdit
  handleTransactionEditCompletion: () => void
}) => {
  const dispatch = useAppDispatch()
  const [editedTransactionCategoryId, setEditedTransactionCategoryId] =
    useState<number>()
  const [editedTransactionType, setEditedTransactionType] = useState<string>()
  const [editedTransactionNote, setEditedTransactionNote] = useState<
    string | null
  >(null)
  const [submitting, setSubmitting] = useState(false)

  const categoryOptions = useReselector(getTransactionCategoryOptions)
  const [transactionEdit, setTransactionEdit] = useState(initialTransactionEdit)

  const selectedCategory = useReselector(
    getTransactionCategorySelector,
    transaction.transactionCategoryId
  )

  const receipts = transaction.receipts

  useEffect(() => {
    const newTransactionEdit = initialTransactionEdit
    if (newTransactionEdit) {
      setEditedTransactionCategoryId(
        newTransactionEdit.transactionCategoryId || undefined
      )
      setEditedTransactionType(newTransactionEdit.transactionType || undefined)
      setEditedTransactionNote(newTransactionEdit.notes)
      setTransactionEdit(newTransactionEdit)
    }
  }, [transactionEdit, initialTransactionEdit])

  const handleApproveTransactionEdit = useCallback(async () => {
    if (!transactionEdit?.id) {
      return
    }

    setSubmitting(true)
    const result = await updateTransactionEdit(transactionEdit.id, {
      approvedOn: Date.now(),
      transactionType: editedTransactionType || null,
      transactionCategoryId: editedTransactionCategoryId || null,
    })(dispatch)
    if (result) {
      setSubmitting(false)
      handleTransactionEditCompletion()
    }
  }, [
    dispatch,
    editedTransactionCategoryId,
    editedTransactionType,
    handleTransactionEditCompletion,
    transactionEdit?.id,
  ])

  const renderAttachedFiles = () => {
    if (!transactionEdit) return null
    if (!receipts || receipts.length === 0) {
      return <i>No attachments</i>
    } else {
      return receipts.map((receipt) => {
        if (!receipt?.signedUrl) {
          return null
        }

        return (
          <p key={receipt.id}>
            <a
              href={receipt.signedUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {receipt.fileDescription}
            </a>
          </p>
        )
      })
    }
  }

  if (!transactionEdit) {
    return <Header as="h6">User has not reviewed transaction yet</Header>
  } else {
    const disabled =
      editedTransactionType === 'business'
        ? !editedTransactionType || !editedTransactionCategoryId
        : !editedTransactionType

    return (
      <Grid stackable doubling className="compact">
        <Grid.Row>
          <Grid.Column width={16}>
            <Header as="h4">
              Proposed Changes for Transaction {transaction.id}
            </Header>
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={16}>
            <Header as="h5">Old Categorization</Header>
          </Grid.Column>
          <Grid.Column width={4}>
            <p>
              <b>Type </b>
            </p>
            <Label>{transaction.type || 'None Selected'}</Label>
          </Grid.Column>
          <Grid.Column width={8}>
            <p>
              <b>Transaction Category</b>
            </p>
            <Label>{selectedCategory?.name || 'None Selected'}</Label>
          </Grid.Column>
        </Grid.Row>

        <Grid.Row>
          <Grid.Column width={16}>
            <Header as="h5">Suggested Edits</Header>
          </Grid.Column>

          <Grid.Column width={4}>
            <p>
              <b>Type </b>
            </p>
            <Dropdown
              placeholder="Select Transaction Type"
              value={editedTransactionType}
              options={TRANSACTION_TYPE_OPTIONS}
              onChange={setEditedTransactionType}
              search
              clearable
              selectOnBlur={false}
              fluid
            />
          </Grid.Column>

          <Grid.Column width={8}>
            <p>
              <b>Transaction Category</b>
            </p>
            <Dropdown
              placeholder="Select Category"
              value={editedTransactionCategoryId}
              options={categoryOptions}
              onChange={setEditedTransactionCategoryId}
              search
              clearable
              fluid
              selectOnBlur={false}
            />
          </Grid.Column>

          <Grid.Column width={4}>
            <p>
              <b>Receipts</b>
            </p>
            {renderAttachedFiles()}
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={8}>
            <p>
              <b>Note</b>
            </p>
            <p>{editedTransactionNote || 'None'}</p>
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={4} floated="right" textAlign="right">
            <Button
              size="mini"
              primary
              onClick={handleApproveTransactionEdit}
              disabled={disabled}
              loading={submitting}
            >
              Approve and Confirm Edit
            </Button>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    )
  }
}

export default TransactionEditReview
